// src/components/MemoryGraph.js
// The "Memory Brain" — a collapsible tree of what the character remembers,
// grouped into dynamic categories. Each entity is colour-coded by type, can be
// muted (excluded from AI context without deleting), and expands to show its
// rich detail: traits, bond metrics, good/bad events, memories and scenes.
//   GET /memory/{botId}/graph  ·  PATCH /memory/{botId}/graph/{nodeId}/mute

import { useEffect, useState } from "react";
import { memory as memoryApi } from "../api";

const NODE_COLORS = {
  user: "#9333EA", character: "#A855F7", npc: "#6366F1", location: "#22C55E",
  scene: "#F59E0B", activity: "#EC4899", object: "#14B8A6", quest: "#EAB308", memory: "#3B82F6",
};
const CAT_ICON = {
  Family: "👪", Friends: "🧑", Enemies: "⚔️", Places: "📍",
  Quests: "🚩", Timeline: "🕒", Items: "📦", Self: "🧠",
};

function subtitle(e) {
  const p = [];
  if (e.relation) p.push(e.relation);
  if (e.bond) p.push(e.bond);
  if (e.rank) p.push(e.rank);
  if (e.age) p.push(`age ${e.age}`);
  if (e.type === "location") p.push("place");
  if (e.type === "quest" && e.status) p.push(e.status);
  if (e.type === "scene") { if (e.location) p.push(e.location); if (e.mood) p.push(e.mood); }
  if (e.type !== "quest" && e.status && e.status !== "unknown" && e.status !== "alive") p.push(e.status);
  return p.join(" · ");
}

function hasDetail(e) {
  return !!(
    e.traits?.length || e.abilities?.length || e.good_events?.length || e.bad_events?.length ||
    e.memories?.length || e.scenes?.length || e.relationship || e.objective || e.love
  );
}

function Chips({ label, items }) {
  if (!items?.length) return null;
  return (
    <div className="brain__d">
      <span className="brain__dLabel">{label}</span>
      <div className="brain__chips">{items.map((t, i) => <span key={i} className="brain__chip">{t}</span>)}</div>
    </div>
  );
}
function Lines({ label, items }) {
  if (!items?.length) return null;
  return (
    <div className="brain__d">
      <span className="brain__dLabel">{label}</span>
      <ul className="brain__lines">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>
    </div>
  );
}

function EntityDetail({ e }) {
  const rel = e.relationship || {};
  const hasBond = rel.trust != null || rel.comfort != null || rel.shared_scenes != null;
  return (
    <div className="brain__detail">
      {e.love && <div className="brain__d"><span className="brain__dLabel">Feeling</span> {e.love}</div>}
      {e.objective && <div className="brain__d"><span className="brain__dLabel">Objective</span> {e.objective}</div>}
      <Chips label="Traits" items={e.traits} />
      <Chips label="Abilities" items={e.abilities} />
      {hasBond && (
        <div className="brain__d">
          <span className="brain__dLabel">Bond</span>
          <div className="brain__metrics">
            {rel.trust != null && <span>trust {rel.trust}</span>}
            {rel.comfort != null && <span>comfort {rel.comfort}</span>}
            {rel.shared_scenes != null && <span>{rel.shared_scenes} scenes</span>}
          </div>
        </div>
      )}
      <Lines label="Good moments" items={e.good_events} />
      <Lines label="Bad moments" items={e.bad_events} />
      <Lines label="Memories" items={e.memories} />
      {e.scenes?.length ? (
        <div className="brain__d">
          <span className="brain__dLabel">Scenes</span>
          {e.scenes.map((s) => (
            <div key={s.id} className="brain__scene">
              <b>{s.name}</b>
              {[s.location, s.mood].filter(Boolean).length ? (
                <span className="brain__sub"> {[s.location, s.mood].filter(Boolean).join(" · ")}</span>
              ) : null}
              {s.activities?.length ? (
                <div className="brain__sub">{s.activities.map((a) => a.name).join(", ")}</div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function MemoryGraph({ botId, botName, onClose }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCats, setOpenCats] = useState({});
  const [openEnt, setOpenEnt] = useState({});

  useEffect(() => {
    memoryApi
      .graph(botId)
      .then((d) => {
        const t = d.tree || { root: null, categories: [] };
        setTree(t);
        const c = {};
        (t.categories || []).forEach((x) => (c[x.name] = true));
        setOpenCats(c);
      })
      .catch(() => setTree({ categories: [] }))
      .finally(() => setLoading(false));
  }, [botId]);

  const setMuted = async (id, muted) => {
    const apply = (m) => (prev) =>
      prev ? { ...prev, categories: prev.categories.map((c) => ({ ...c, items: c.items.map((it) => (it.id === id ? { ...it, muted: m } : it)) })) } : prev;
    setTree(apply(muted));
    try {
      await memoryApi.muteNode(botId, id, muted);
    } catch (_) {
      setTree(apply(!muted)); // revert
    }
  };

  const cats = tree?.categories || [];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal brain" onClick={(e) => e.stopPropagation()}>
        <div className="brain__head">
          <span>🧠 {botName}'s memory</span>
          <button onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="brain__body">
          {loading ? (
            <div className="chat__loading">Loading…</div>
          ) : cats.length === 0 ? (
            <div className="empty" style={{ padding: "24px 0" }}>No memories yet — keep chatting and people, places &amp; scenes will grow here.</div>
          ) : (
            <>
              <div className="brain__root">
                <span className="brain__dot" style={{ background: NODE_COLORS.user }} />
                {tree?.root?.name || "You"}
              </div>
              {cats.map((cat) => (
                <div key={cat.name} className="brain__cat">
                  <button className="brain__catHead" onClick={() => setOpenCats((s) => ({ ...s, [cat.name]: !s[cat.name] }))}>
                    <span>{CAT_ICON[cat.name] || "🏷️"}</span>
                    <b>{cat.name}</b>
                    <span className="brain__count">{cat.items.length}</span>
                    <span style={{ marginLeft: "auto", color: "var(--text-dim)" }}>{openCats[cat.name] ? "▾" : "▸"}</span>
                  </button>
                  {openCats[cat.name] &&
                    cat.items.map((e) => {
                      const open = !!openEnt[e.id];
                      const expandable = hasDetail(e);
                      return (
                        <div key={e.id} className="brain__entWrap">
                          <div className="brain__ent">
                            <span className="brain__dot" style={{ background: NODE_COLORS[e.type] || "#888" }} />
                            <button
                              className="brain__entText"
                              onClick={() => expandable && setOpenEnt((s) => ({ ...s, [e.id]: !s[e.id] }))}
                            >
                              <span className={`brain__entName ${e.muted ? "muted" : ""}`}>{e.name}</span>
                              {subtitle(e) && <span className="brain__sub">{subtitle(e)}</span>}
                            </button>
                            {expandable && <span className="brain__chev">{open ? "▾" : "▸"}</span>}
                            <button
                              className={`brain__toggle ${e.muted ? "" : "on"}`}
                              title={e.muted ? "Muted — AI ignores this" : "Active — AI remembers this"}
                              onClick={() => setMuted(e.id, !e.muted)}
                            />
                          </div>
                          {open && <EntityDetail e={e} />}
                        </div>
                      );
                    })}
                </div>
              ))}
            </>
          )}
        </div>
        <div className="brain__foot">Tap a name for detail · toggle off to make {botName} forget it in replies (without deleting).</div>
      </div>
    </div>
  );
}
